import { Dispatch } from '@reduxjs/toolkit';
import ingestSlice, { MappingState, ProgressState } from './ingestSlice';
import { pollMappingValidation, submitMapping } from '../../api/ingest';
import { translateToColumnMapping } from './mappingHelpers';
import AlertActions from '../alerts/actions';
import { AlertType } from '../../common/domain/ApplicationAlert';
import { ErrorResponse } from '../../common/domain/responses';
import { validateMappingSubmission } from '../../pages/IngestPage/validation';

const { actions } = ingestSlice;

export default class MappingActions {
  constructor(private dispatch: Dispatch) {}

  toggleHeader() {
    this.dispatch(actions.toggleHeader());
  }

  cancelMapping() {
    this.dispatch(actions.cancellingMapping());
    setTimeout(() => {
      this.dispatch(actions.discardMapping());
    }, 1500);
  }

  clear(position: number) {
    this.dispatch(actions.clearMapping({ position }));
  }

  update(position: number, col: string) {
    this.dispatch(actions.updateMapping({ position, col }));
  }

  submitMapping(state: MappingState) {
    const headerErrors = validateMappingSubmission(
      translateToColumnMapping(state.mapping),
      state.type,
    );
    if (headerErrors.length > 0) {
      new AlertActions(this.dispatch).add({
        header: 'Mapping Error',
        body: headerErrors.toString(),
        type: AlertType.ERROR,
      });
    } else {
      this.dispatch(
        actions.updateProgress({
          message: 'Submitting mapping',
          type: 'mapping',
          value: 50,
        }),
      );
      submitMapping({
        hasHeader: state.hasHeader,
        groupId: state.group.id,
        ingestId: state.ingestId,
        columnMap: translateToColumnMapping(state.mapping),
      })
        .then(() => {
          this.dispatch(
            actions.updateProgress({
              message: 'Performing validation...',
              type: 'mapping',
              value: 100,
            }),
          );
          return pollMappingValidation(
            state.ingestId,
            state.group.id,
            (result) => {
              return result.mappedAt != null;
            },
            (result) => {
              const errors = errorHandler(result);
              return errors != null;
            },
          );
        })
        .then((result) => {
          this.dispatch(
            actions.initializeVerification({
              validatedAt: 0,
              verificationSuccess:
                result.errors == null ||
                (result.errors.length == 0 && result.duplicateCount == 0)
                  ? true
                  : false,
              errorsAccepted: false,
              numberOfErrors: result.validationErrorCount,
              rowsWithErrors: result.rowsWithErrors,
              numberOfDuplicateRows: result.duplicateCount,
              rowsUploaded: result.rowsUploaded,
              isCancellable: true,
              cancelling: false,
              ingestId: state.ingestId,
              group: state.group,
            }),
          );
        })
        .catch((result: ErrorResponse) => {
          this.dispatch(actions.initializeMapping(state));
          new AlertActions(this.dispatch).add({
            header: 'Mapping Error',
            body: result.error,
            type: AlertType.ERROR,
          });
        });
    }
  }
}

function errorHandler(result) {
  if (result.errors && result.errors.length > 0)
    return result.errors.map((error) =>
      error.message ? error.message.toString() : error.toString(),
    );
  return null;
}
